import { useState, useEffect, useRef, ElementType, MouseEvent } from 'react';
import Box from '@prezzee/ribbon-ui/lib/components/Box';
import Button from '@prezzee/ribbon-ui/lib/components/Button';
import debounce from 'utils/debounce';

export type ClampLinesProps = {
  text: string;
  id: string;
  lines?: number;
  ellipsis?: string;
  buttons?: boolean;
  moreText?: string;
  lessText?: string;
  className?: string;
  delay?: number;
  stopPropagation?: boolean;
  innerElement?: ElementType;
};

const ClampLinesProps = ({
  text: textProp,
  id,
  lines = 3,
  ellipsis = '...',
  buttons = true,
  moreText = 'Read more',
  lessText = 'Read less',
  className: classNameProp,
  delay = 300,
  stopPropagation: stopPropagationProp,
  innerElement = 'div',
}: ClampLinesProps) => {
  const [expanded, setExpanded] = useState(true);
  const [noClamp, setNoClamp] = useState(false);
  const [text, setText] = useState(textProp.substring(0, 20));

  const elementRef = useRef<HTMLDivElement>(null);
  const originalRef = useRef<string>(textProp);
  const watchRef = useRef<boolean>(true);
  const lineHeightRef = useRef<number>(0);
  const startRef = useRef<number>(0);
  const middleRef = useRef<number>(0);
  const endRef = useRef<number>(0);
  const uuidRef = useRef<string>(id);

  const ssr = typeof window === 'undefined';

  const action = () => {
    if (watchRef.current) {
      setNoClamp(false);
      clampLines();
      setExpanded(prevExpanded => !prevExpanded);
    }
  };

  const clampLines = () => {
    if (!elementRef.current) return;

    setText('');

    let maxHeight = lineHeightRef.current * lines + 1;

    startRef.current = 0;
    middleRef.current = 0;
    endRef.current = originalRef.current.length;

    while (startRef.current <= endRef.current) {
      middleRef.current = Math.floor((startRef.current + endRef.current) / 2);
      if (elementRef.current) {
        elementRef.current.innerText = originalRef.current.slice(0, middleRef.current);
      }
      if (middleRef.current === originalRef.current.length) {
        setText(originalRef.current);
        setNoClamp(true);
        return;
      }

      moveMarkers(maxHeight);
    }

    if (elementRef.current) {
      elementRef.current.innerText = originalRef.current.slice(0, middleRef.current - 5) + getEllipsis();
    }
    setText(originalRef.current.slice(0, middleRef.current - 5) + getEllipsis());
  };

  const moveMarkers = (maxHeight: number) => {
    if (elementRef.current && elementRef.current.clientHeight <= maxHeight) {
      startRef.current = middleRef.current + 1;
    } else {
      endRef.current = middleRef.current - 1;
    }
  };

  const getEllipsis = () => {
    return watchRef.current && !noClamp ? ellipsis : '';
  };

  const getButton = () => {
    if (noClamp || !buttons) return null;

    let buttonText = watchRef.current ? moreText : lessText;

    return (
      <Button
        variant="link"
        onClick={clickHandler}
        aria-controls={`clamped-content-${uuidRef.current}`}
        aria-expanded={!expanded}
      >
        {buttonText}
      </Button>
    );
  };

  const clickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    stopPropagationProp && e.stopPropagation();

    watchRef.current = !watchRef.current;
    watchRef.current ? clampLines() : setText(originalRef.current);

    setExpanded(prevExpanded => !prevExpanded);
  };

  useEffect(() => {
    if (textProp && !ssr) {
      if (elementRef.current) {
        lineHeightRef.current = elementRef.current.clientHeight + 1;
      }
      clampLines();

      if (watchRef.current) {
        window.addEventListener('resize', debounce(action, delay));
      }
    }

    return () => {
      if (!ssr) {
        window.removeEventListener('resize', debounce(action, delay));
      }
    };
  }, [textProp]);

  useEffect(() => {
    if (text !== textProp) {
      originalRef.current = textProp;
      clampLines();
    }
  }, [text]);

  if (!textProp) {
    return null;
  }

  const InnerElement = innerElement;

  return (
    <Box className={classNameProp}>
      <InnerElement ref={elementRef} id={`clamped-content-${uuidRef.current}`} aria-hidden={expanded}>
        {text}
      </InnerElement>
      {getButton()}
    </Box>
  );
};

export default ClampLinesProps;
